import React, {
  memo,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { getUserDetails } from "../../api/users";
import { useUserContext } from "../../contexts/UserContext";
import { UserStatus } from "../../constants";
import { STATIC_URL } from "../../constants";
import "../styles/UserPanel.css";

interface UserViewPanelProps {
  nickname: string;
  avatar?: string;
  fullname?: string;
  vkUserId?: string;
  onClose: () => void;
  onUserFilterChange?: (nickname: string | null) => void;
  onOpenChatWithUser?: (nickname: string) => void;
}

const buildVKProfileUrl = (vkUserId?: string): string | null => {
  const value = vkUserId?.trim();
  if (!value) {
    return null;
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  const path = value.replace(/^vk\.com\//i, "");
  if (/^\d+$/.test(path)) {
    return `https://vk.com/id${path}`;
  }

  return `https://vk.com/${path}`;
};

const UserViewPanel: React.FC<UserViewPanelProps> = memo(
  ({
    nickname,
    avatar,
    fullname,
    vkUserId,
    onClose,
    onUserFilterChange,
    onOpenChatWithUser,
  }) => {
    const { t } = useTranslation();
    const { user } = useUserContext();
    const isAdmin = user?.status === UserStatus.admin;
    const [adminUserDetails, setAdminUserDetails] = useState<{
      fullname?: string;
      email?: string;
      vkUserId?: string;
    } | null>(null);
    const [emailCopied, setEmailCopied] = useState(false);
    const copyTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
      let isCancelled = false;
      setEmailCopied(false);

      if (!isAdmin) {
        setAdminUserDetails(null);
        return () => {
          isCancelled = true;
        };
      }

      const loadAdminUserDetails = async () => {
        try {
          const details = await getUserDetails(nickname);
          if (!isCancelled) {
            setAdminUserDetails({
              fullname: details.fullname,
              email: details.email,
              vkUserId: details.vkUserId,
            });
          }
        } catch (error) {
          console.error("Failed to load admin user details:", error);
          if (!isCancelled) {
            setAdminUserDetails(null);
          }
        }
      };

      loadAdminUserDetails();

      return () => {
        isCancelled = true;
      };
    }, [isAdmin, nickname]);

    useEffect(() => {
      return () => {
        if (copyTimeoutRef.current) {
          window.clearTimeout(copyTimeoutRef.current);
        }
      };
    }, []);

    const displayedFullname = adminUserDetails?.fullname || fullname;
    const displayedEmail = isAdmin ? adminUserDetails?.email : undefined;
    const viewedVkUserId = adminUserDetails?.vkUserId || vkUserId;
    const vkProfileUrl = useMemo(
      () => buildVKProfileUrl(viewedVkUserId),
      [viewedVkUserId],
    );

    const avatarUrl = useMemo(() => {
      if (!avatar) return null;

      if (avatar.startsWith("http://") || avatar.startsWith("https://")) {
        return avatar;
      }

      return `${STATIC_URL}${avatar.replace(/^\//, "")}`;
    }, [avatar]);

    const userInitial = useMemo(
      () => nickname?.charAt(0).toUpperCase() || "U",
      [nickname],
    );

    const handleOverlayClick = useCallback(
      (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      },
      [onClose],
    );

    const handlePanelClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    const handleViewContributions = useCallback(() => {
      if (onUserFilterChange) {
        onUserFilterChange(nickname);
        onClose();
      }
    }, [nickname, onUserFilterChange, onClose]);

    const handleSendMessage = useCallback(() => {
      if (!onOpenChatWithUser) {
        return;
      }

      onOpenChatWithUser(nickname);
      onClose();
    }, [nickname, onClose, onOpenChatWithUser]);

    const handleOpenVKProfile = useCallback(() => {
      if (!vkProfileUrl) {
        return;
      }

      window.open(vkProfileUrl, "_blank", "noopener,noreferrer");
    }, [vkProfileUrl]);

    const copyTextToClipboard = useCallback(async (text: string) => {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }

      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.top = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();

      try {
        document.execCommand("copy");
      } finally {
        document.body.removeChild(textarea);
      }
    }, []);

    const handleCopyEmail = useCallback(async () => {
      if (!displayedEmail) {
        return;
      }

      try {
        await copyTextToClipboard(displayedEmail);
        setEmailCopied(true);

        if (copyTimeoutRef.current) {
          window.clearTimeout(copyTimeoutRef.current);
        }

        copyTimeoutRef.current = window.setTimeout(() => {
          setEmailCopied(false);
          copyTimeoutRef.current = null;
        }, 1600);
      } catch (error) {
        console.error("Failed to copy email:", error);
      }
    }, [copyTextToClipboard, displayedEmail]);

    const handleWriteEmail = useCallback(() => {
      if (!displayedEmail) {
        return;
      }

      window.location.href = `mailto:${encodeURIComponent(displayedEmail)}`;
    }, [displayedEmail]);

    if (!nickname) return null;

    return (
      <div className="user-panel-overlay" onClick={handleOverlayClick}>
        <div className="user-panel user-view-panel" onClick={handlePanelClick}>
          <div className="user-panel-header">
            <h3>{t("user.userProfile")}</h3>
            <div className="header-actions">
              <button className="close-button" onClick={onClose}>
                ×
              </button>
            </div>
          </div>

          <div className="user-panel-content">
            <div className="user-avatar-section">
              <div className="user-avatar-large">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={`${nickname}'s avatar`}
                    className="avatar-image"
                  />
                ) : (
                  <div className="avatar-placeholder">{userInitial}</div>
                )}
              </div>
              <h4 className="user-nickname">{nickname}</h4>
              {displayedFullname && (
                <p className="user-fullname">{displayedFullname}</p>
              )}
              {displayedEmail && (
                <div className="admin-user-email-row">
                  <span className="admin-user-email" title={displayedEmail}>
                    {displayedEmail}
                  </span>
                  <button
                    type="button"
                    className={`email-action-button copy-email-button${
                      emailCopied ? " copied" : ""
                    }`}
                    onClick={handleCopyEmail}
                    aria-label={
                      emailCopied ? t("user.emailCopied") : t("user.copyEmail")
                    }
                    title={
                      emailCopied ? t("user.emailCopied") : t("user.copyEmail")
                    }
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/images/copy.png`}
                      alt=""
                      className="email-action-icon"
                      aria-hidden="true"
                    />
                  </button>
                  <button
                    type="button"
                    className="email-action-button write-email-button"
                    onClick={handleWriteEmail}
                    aria-label={t("user.writeEmail")}
                    title={t("user.writeEmail")}
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/images/write.png`}
                      alt=""
                      className="email-action-icon"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              )}
            </div>

            {(isAdmin && onOpenChatWithUser) ||
            (isAdmin && vkProfileUrl) ||
            onUserFilterChange ? (
              <div className="user-actions">
                {isAdmin && onOpenChatWithUser && (
                  <button
                    className="send-message-button"
                    onClick={handleSendMessage}
                  >
                    {t("chat.sendMessage")}
                  </button>
                )}
                {isAdmin && vkProfileUrl && (
                  <button
                    className="vk-profile-button"
                    onClick={handleOpenVKProfile}
                  >
                    {t("openInVK")}
                  </button>
                )}
                {onUserFilterChange && (
                  <button
                    className="view-contributions-button"
                    onClick={handleViewContributions}
                  >
                    {t("user.viewContributions")}
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  },
);

UserViewPanel.displayName = "UserViewPanel";

export default UserViewPanel;
